import { useState } from 'react';
import {
  REACT_APP_BLOCK_EXPLORER_OPEN_ZK_API,
  REACT_APP_ETH_RPC,
  REACT_APP_OPENZK_RPC,
  REACT_APP_OZETH_L2_TOKEN,
  REACT_APP_OZUSD_L2_TOKEN,
} from '../Config/Config';
import axios from 'axios';
import Web3 from 'web3';
import { BrowserProvider, L1Signer, Provider } from 'zksync-ethers';
import { Connection } from '@wagmi/core';
import BigNumber from 'bignumber.js';

export enum Status {
  'claimed' = 1,
  'pending' = 2,
  'claim' = 3,
}

type Transaction = {
  txHash: string;
  description: string;
  status: Status;
};

export function useL2Transactions(address: string) {
  const web3 = new Web3(REACT_APP_OPENZK_RPC);

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);

  const loadL2Transactions = async (connections: Connection) => {
    if (!address || !connections) {
      return;
    }

    if (!connections[0]?.connector?.getProvider) {
      return;
    }

    const latestBlock = await web3.eth.getBlockNumber();

    const walletProvider = await connections[0].connector.getProvider();

    const provider = new BrowserProvider(walletProvider);

    const zkProvider = new Provider(REACT_APP_OPENZK_RPC);

    const signer = L1Signer.from(await provider.getSigner(), zkProvider);

    setLoading(true);

    let fromBlockEth = 1;
    let fromBlockUsd = 1;
    const BLOCK_STEP = 500;

    let resultEth = [];
    let resultUsd = [];

    while (fromBlockEth <= Number(latestBlock)) {
      let toBlock = Math.min(
        fromBlockEth + BLOCK_STEP - 1,
        Number(latestBlock),
      );
      const result = await axios.get(REACT_APP_BLOCK_EXPLORER_OPEN_ZK_API, {
        params: {
          module: 'logs',
          action: 'getLogs',
          address: REACT_APP_OZETH_L2_TOKEN,
          fromBlock: fromBlockEth,
          toBlock: toBlock,
          topic0:
            '0x2717ead6b9200dd235aad468c9809ea400fe33ac69b5bfaa6d3e90fc922b6398',
        },
      });

      resultEth = [...resultEth, result.data.result].flat();

      fromBlockEth = toBlock + 1;
    }

    while (fromBlockUsd <= Number(latestBlock)) {
      let toBlockUsd = Math.min(
        fromBlockUsd + BLOCK_STEP - 1,
        Number(latestBlock),
      );
      const result = await axios.get(REACT_APP_BLOCK_EXPLORER_OPEN_ZK_API, {
        params: {
          module: 'logs',
          action: 'getLogs',
          address: REACT_APP_OZUSD_L2_TOKEN,
          fromBlock: fromBlockUsd,
          toBlock: toBlockUsd,
          topic0:
            '0x9b5b9a05e4726d8bb959f1440e05c6b8109443f2083bc4e386237d7654526553',
        },
      });

      resultUsd = [...resultUsd, result.data.result].flat();

      fromBlockUsd = toBlockUsd + 1;
    }

    const reversedList = resultEth?.map((e, i, a) => a[a.length - 1 - i]);

    const reversedUsdList = resultUsd?.map((e, i, a) => a[a.length - 1 - i]);

    const transactionHistory = [];

    await Promise.all([
      reversedUsdList?.map(async (event) => {
        const bridgeData = web3.eth.abi.decodeLog(
          [
            { indexed: true, type: 'address', name: '_account' },
            { indexed: false, type: 'uint256', name: '_amount' },
          ],
          event.data,
          [event.topics[1]],
        );

        try {
          if (address === bridgeData._account) {
            const isWithdrawalFinalized = await signer.isWithdrawalFinalized(
              event.transactionHash,
            );
            const details = await zkProvider.getTransactionDetails(
              event.transactionHash,
            );

            if (!isWithdrawalFinalized) {
              if (!!details.ethExecuteTxHash) {
                transactionHistory.push({
                  txHash: event.transactionHash,
                  description: `Bridge ${BigNumber(web3.utils.fromWei(bridgeData._amount as number, 'ether')).toFixed(4, 1)} ozUSD`,
                  status: Status.claim,
                });
              } else {
                // it means withdrawal is finalized but not executed so not claimable on l1
                transactionHistory.push({
                  txHash: event.txHash,
                  description: `Bridge ${BigNumber(web3.utils.fromWei(bridgeData._amount as number, 'ether')).toFixed(4, 1)} ozUSD`,
                  status: Status.pending,
                });
              }
            }
          }
        } catch (error) {
          console.log(error.message);
          if (error.message === 'Log proof not found!') {
            transactionHistory.push({
              txHash: event.txHash,
              description: `Bridge ${BigNumber(web3.utils.fromWei(bridgeData._amount as number, 'ether')).toFixed(4, 1)} ozUSD`,
              status: Status.pending,
            });
          }
        }
      }),
      reversedList?.map(async (event) => {
        const bridgeData = web3.eth.abi.decodeLog(
          [
            { indexed: true, type: 'address', name: 'from' },
            { indexed: true, type: 'address', name: 'recipient' },
            { indexed: false, type: 'uint256', name: 'amount' },
          ],
          event.data,
          [event.topics[1], event.topics[2]],
        );

        try {
          if (address === bridgeData.from) {
            const isWithdrawalFinalized = await signer.isWithdrawalFinalized(
              event.transactionHash,
            );

            const details = await zkProvider.getTransactionDetails(
              event.transactionHash,
            );
            if (!isWithdrawalFinalized) {
              // it means is finalized but not executable
              if (!!details.ethExecuteTxHash) {
                transactionHistory.push({
                  txHash: event.transactionHash,
                  description: `Bridge ${BigNumber(web3.utils.fromWei(bridgeData.amount as number, 'ether')).toFixed(4, 1)} ozETH`,
                  status: Status.claim,
                });
              } else {
                transactionHistory.push({
                  txHash: event.txHash,
                  description: `Bridge ${BigNumber(web3.utils.fromWei(bridgeData.amount as number, 'ether')).toFixed(4, 1)} ozETH`,
                  status: Status.pending,
                });
              }
            }
          }
        } catch (error) {
          console.log(error.message);
          if (error.message === 'Log proof not found!') {
            transactionHistory.push({
              txHash: event.txHash,
              description: `Bridge ${BigNumber(web3.utils.fromWei(bridgeData.amount as number, 'ether')).toFixed(4, 1)} ozETH`,
              status: Status.pending,
            });
          }
        }
      }),
    ]);

    setLoading(false);

    setTransactions(transactionHistory);
  };

  const resetTransactions = async () => {
    setTransactions([]);
  };

  return { loading, transactions, loadL2Transactions, resetTransactions };
}
